import { datadogRum } from '@datadog/browser-rum';
import { getConsentKey } from './cookie-consent-helpers';

declare global {
  interface Window {
    Relyance?: {
      agent?: {
        addEventListener: (
          event: string,
          callback: (e: RelyanceConsentEvent) => void
        ) => void;
        showPreferenceCenter?: () => void;
        consentInterfaceTemplate?: Record<string, unknown>;
        relyanceConsentStorageService?: {
          readConsentFromBrowser: () => RelyanceConsentCookie | null;
        };
      };
      showPreferenceCenter?: () => void;
    };
  }
}

interface RelyanceConsentEvent {
  consentStatus: Record<string, boolean>;
}

interface RelyanceConsentPurpose {
  purposeId: string;
  name: string;
  visible: boolean;
  checked: boolean;
  alwaysActive?: boolean;
}

interface RelyanceConsentCookie {
  model?: string;
  consentPurposes?: RelyanceConsentPurpose[];
  timestamp?: number;
  consentStorageEnabled?: boolean;
  isDefaultConsent?: boolean;
}

export interface ConsentService {
  updateUserConsent: (key: string, value: boolean) => void;
}

/**
 * Mapping from our internal category names to Relyance processing activity names.
 * These should match the processing activities configured in Relyance admin console.
 */
export const CATEGORY_MAPPING = Object.freeze({
  performance: ['data analytics'],
  functional: ['functional'],
  targeting: ['marketing', 'advertising', 'targeting'],
} as const);

type CategoryKeys = keyof typeof CATEGORY_MAPPING;

let _consentMissingReported = false;
let _resetListenerRegistered = false;

const reportConsentMissingWithAgent = () => {
  if (_consentMissingReported) {
    return;
  }
  _consentMissingReported = true;
  const message =
    'Relyance agent loaded but readConsentFromBrowser() returned null';
  console.warn(message);
  datadogRum.addError(new Error(message), {
    source: 'relyance',
    sdkLoaded: true,
    method: 'readConsentFromBrowser',
  });
};

const Relyance = {
  CATEGORY_MAPPING,
  RELYANCE_READY_EVENT_NAME: 'RELYANCE_CONSENT_UPDATE',
  RELYANCE_CONSENT_UPDATE_NAME: 'RELYANCE_CONSENT_UPDATE',

  /**
   * Invokes a function when Relyance is ready to be read from.
   *
   * Readiness requires both `agent` (for event listeners) and
   * `relyanceConsentStorageService.readConsentFromBrowser` (for reading
   * stored consent). Relyance can expose `agent` before the storage
   * service attaches, and emits no event when the service finishes
   * attaching, so a callback that fires too early would read null and
   * have no way to retry.
   * @param {function} fn - The function to be invoked
   */
  onRelyanceReady(fn: () => void) {
    const isReady = () =>
      !!window.Relyance?.agent?.relyanceConsentStorageService
        ?.readConsentFromBrowser;
    if (isReady()) {
      fn();
      return;
    }
    // Wait for Relyance to load by polling
    const checkInterval = setInterval(() => {
      if (isReady()) {
        clearInterval(checkInterval);
        fn();
      }
    }, 100);
    // Stop polling after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
  },

  /**
   * Reads the user's consent state via the Relyance SDK.
   * Returns null when the SDK is not yet ready; callers should retry on
   * the next render or in response to RELYANCE_CONSENT_UPDATE.
   */
  readConsent(): RelyanceConsentCookie | null {
    const service = window.Relyance?.agent?.relyanceConsentStorageService;
    if (!service?.readConsentFromBrowser) {
      return null;
    }

    if (!_resetListenerRegistered && window.Relyance?.agent?.addEventListener) {
      _resetListenerRegistered = true;
      window.Relyance.agent.addEventListener('RELYANCE_CONSENT_UPDATE', () => {
        _consentMissingReported = false;
      });
    }

    const consent = service.readConsentFromBrowser() ?? null;
    if (!consent) {
      reportConsentMissingWithAgent();
    }
    return consent;
  },

  /**
   * Gets the allowed categories from the Relyance consent cookie.
   * @returns {string[]} - Array of allowed category names (lowercase)
   */
  getAllowedCategories(): string[] {
    const consentData = this.readConsent();

    if (consentData?.consentPurposes) {
      return consentData.consentPurposes
        .filter((purpose) => purpose.checked)
        .map((purpose) => (purpose.name || purpose.purposeId).toLowerCase());
    }

    return [];
  },

  /**
   * Stores the user's cookie preferences using the consent service.
   * @param {ConsentService} consentService - The consent service to update user consent.
   */
  storeCookiePreferences(consentService: ConsentService) {
    const userPreferences = this.getUserPreferences();
    Object.entries(userPreferences).forEach(([category, value]) => {
      const consentKey = getConsentKey(category);
      consentService.updateUserConsent(consentKey, value);
    });
  },

  /**
   * Gets the user's consent for a specific category.
   * @param {CategoryKeys} category - The category to get consent for.
   * @returns {boolean} - The user's consent for the category.
   */
  getConsentForCategory(category: CategoryKeys): boolean {
    const categoryNames = CATEGORY_MAPPING[category] || [];
    const allowedCategories = this.getAllowedCategories();
    return categoryNames.some((name) => allowedCategories.includes(name));
  },

  /**
   * Gets the user's preferences for all categories.
   * @returns {Record<CategoryKeys, boolean>} - The user's preferences for all categories.
   */
  getUserPreferences(): Record<CategoryKeys, boolean> {
    const allowedCategories = this.getAllowedCategories();
    const userPreferences = {} as Record<CategoryKeys, boolean>;

    for (const category in CATEGORY_MAPPING) {
      const typedCategory = category as CategoryKeys;
      const categoryNames = CATEGORY_MAPPING[typedCategory];
      userPreferences[typedCategory] = categoryNames.some((name) =>
        allowedCategories.includes(name)
      );
    }

    return userPreferences;
  },

  /**
   * Determines if the user has interacted with the cookie consent banner.
   * For Relyance, we check if the rly_consent cookie exists and is not default consent.
   * @returns {boolean} - True if consent cookie exists with user selection, false otherwise.
   */
  hasBannerInteraction(): boolean {
    const consentData = this.readConsent();
    return consentData !== null && consentData.isDefaultConsent !== true;
  },

  /**
   * Checks if consent data is available from Relyance.
   * @returns {boolean} - True if consent data is available, false otherwise.
   */
  hasConsentData(): boolean {
    return this.readConsent() !== null;
  },

  _resetConsentMissingState() {
    _consentMissingReported = false;
    _resetListenerRegistered = false;
  },

  _preferenceCenterPollInterval: undefined as
    ReturnType<typeof setInterval> | undefined,

  /**
   * Opens the Relyance preference center dialog.
   *
   * The Relyance SDK fetches its consent template asynchronously after the
   * script loads.  If showPreferenceCenter is called before that fetch
   * completes, the SDK silently resolves without rendering anything.  We
   * guard against this by polling for the template before invoking the SDK.
   */
  showPreferenceCenter() {
    const showFn =
      window.Relyance?.showPreferenceCenter ??
      window.Relyance?.agent?.showPreferenceCenter;

    if (!showFn) {
      const message = window.Relyance
        ? 'Relyance preference center method not available'
        : 'Relyance SDK not loaded';
      console.warn(message);
      datadogRum.addError(new Error(message), { source: 'relyance' });
      return;
    }

    if (window.Relyance!.agent?.consentInterfaceTemplate) {
      showFn.call(window.Relyance);
      return;
    }

    // Already polling from a previous click — don't create another interval
    if (this._preferenceCenterPollInterval) {
      return;
    }

    // Template not yet loaded — poll until the SDK is fully initialized
    const POLL_INTERVAL_MS = 100;
    const MAX_WAIT_MS = 5000;
    let elapsed = 0;
    this._preferenceCenterPollInterval = setInterval(() => {
      elapsed += POLL_INTERVAL_MS;
      if (window.Relyance?.agent?.consentInterfaceTemplate) {
        clearInterval(this._preferenceCenterPollInterval);
        this._preferenceCenterPollInterval = undefined;
        const fn =
          window.Relyance.showPreferenceCenter ??
          window.Relyance.agent?.showPreferenceCenter;
        fn?.call(window.Relyance);
      } else if (elapsed >= MAX_WAIT_MS) {
        clearInterval(this._preferenceCenterPollInterval);
        this._preferenceCenterPollInterval = undefined;
        const message = 'Relyance preference center not ready after timeout';
        console.warn(message);
        datadogRum.addError(new Error(message), { source: 'relyance' });
      }
    }, POLL_INTERVAL_MS);
  },
};

export default Relyance;
